import { create } from "zustand";
import { isEitaaMiniApp } from "@/utils/utilities";

/**
 * Whether the mini-app may message this user outside the app.
 *
 * `unknown` means we have never had an answer — not that it was refused.
 * `unsupported` means this client is too old to be asked, which is permanent
 * for the session and must never be retried in a loop.
 */
export type WriteAccessStatus =
  | "unknown"
  | "granted"
  | "denied"
  | "unsupported";

interface PersistedWriteAccess {
  status: WriteAccessStatus;
  /** Epoch ms of the last time the user was actually shown the prompt. */
  lastAskedAt: number | null;
}

interface WriteAccessState extends PersistedWriteAccess {
  /** A request is in flight; the SDK rejects a second one while it is. */
  requesting: boolean;
  /**
   * Ask the client for permission. Resolves to whether we hold it afterwards.
   * Safe to call when it is already granted: the client answers without
   * putting anything on screen.
   */
  request: () => Promise<boolean>;
  /** Whether asking now would be useful — see {@link shouldAsk}. */
  shouldAsk: () => boolean;
}

const STORAGE_KEY = "write-access";

/**
 * How long to leave a refusal alone before offering again.
 *
 * Short enough that a user who refused by reflex gets another chance the same
 * day, long enough that reopening the app a few times in a row does not put the
 * prompt back on screen each time.
 */
const RETRY_AFTER_DENIED = 1000 * 60 * 60; // 1 hour

/** Safety valve for a client that never calls back. See {@link RequestOutcome}. */
const ANSWER_TIMEOUT = 1000 * 60 * 3;

/**
 * What one call to the client produced. `in-flight` and `abandoned` are not
 * answers — they say we failed to ask, which is different from being refused.
 */
type RequestOutcome =
  | "granted"
  | "denied"
  | "unsupported"
  | "in-flight"
  | "abandoned";

function read(): PersistedWriteAccess {
  const empty: PersistedWriteAccess = { status: "unknown", lastAskedAt: null };
  if (typeof window === "undefined") return empty;
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return empty;
    const parsed = JSON.parse(raw) as Partial<PersistedWriteAccess>;
    const status = parsed.status;
    // `unsupported` is a fact about the client build, not about the user, so
    // it is never restored — a client that has since updated can be asked.
    const restored: WriteAccessStatus =
      status === "granted" || status === "denied" ? status : "unknown";
    return {
      status: restored,
      lastAskedAt:
        typeof parsed.lastAskedAt === "number" ? parsed.lastAskedAt : null,
    };
  } catch {
    return empty;
  }
}

function write(value: PersistedWriteAccess) {
  if (typeof window === "undefined") return;
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(value));
  } catch {
    // A full or blocked storage only costs us the memory of the answer.
  }
}

const initial = read();

export const useWriteAccessStore = create<WriteAccessState>((set, get) => ({
  status: initial.status,
  lastAskedAt: initial.lastAskedAt,
  requesting: false,

  shouldAsk: () => {
    const { status, lastAskedAt, requesting } = get();
    if (requesting) return false;
    // Outside the mini-app there is no client to ask.
    if (!isEitaaMiniApp()) return false;
    if (status === "granted" || status === "unsupported") return false;
    if (status === "denied") {
      return lastAskedAt === null || Date.now() - lastAskedAt > RETRY_AFTER_DENIED;
    }
    return true;
  },

  request: async () => {
    if (get().requesting) return false;

    const webApp =
      typeof window !== "undefined" ? window.Eitaa?.WebApp : undefined;
    const ask = webApp?.requestWriteAccess;

    if (!ask) {
      set({ status: "unsupported" });
      return false;
    }

    set({ requesting: true });

    const outcome = await new Promise<RequestOutcome>((resolve) => {
      let settled = false;
      const finish = (value: RequestOutcome) => {
        if (settled) return;
        settled = true;
        clearTimeout(timer);
        resolve(value);
      };

      // The prompt is modal and the user may sit on it, or background the app
      // entirely, so this is only a safety valve: without it a callback that
      // never arrives would leave `requesting` true and block every later ask.
      const timer = setTimeout(() => finish("abandoned"), ANSWER_TIMEOUT);

      try {
        // Called off the WebApp object, which the SDK reads as `this`.
        ask.call(webApp, (granted) =>
          finish(granted ? "granted" : "denied"),
        );
      } catch (error) {
        const message = error instanceof Error ? error.message : String(error);
        finish(
          message === "WebAppMethodUnsupported" ? "unsupported" : "in-flight",
        );
      }
    });

    if (outcome === "unsupported") {
      // A fact about the client build, not the user — not worth persisting.
      set({ status: "unsupported", requesting: false });
      return false;
    }

    // Someone else is already mid-prompt, or the client never answered. Either
    // way the user has not refused us, so nothing is recorded — recording a
    // denial here would silence the prompt for an hour over a race.
    if (outcome === "in-flight" || outcome === "abandoned") {
      set({ requesting: false });
      return false;
    }

    const next: PersistedWriteAccess = {
      status: outcome,
      lastAskedAt: Date.now(),
    };
    set({ ...next, requesting: false });
    write(next);

    return outcome === "granted";
  },
}));
